import type { ReactNode } from 'react';
import { NavLink, type NavLinkRenderProps } from 'react-router-dom';
import { useNotifications, useProfile } from '../lib/ordering.js';
import { useAuthStore } from '../stores/auth.js';
import { useT } from '../lib/i18n.js';

/**
 * Line icons rather than emoji.
 *
 * An emoji is drawn by the platform, so the same tab bar is a different weight
 * and a different colour on every phone in the plant — and none of them take
 * the green the active tab is supposed to carry. These inherit `currentColor`,
 * which is the whole reason the capsule works.
 */
const ICON = 'h-[21px] w-[21px]';

function stroke(path: ReactNode) {
  return (
    <svg
      aria-hidden
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={ICON}
    >
      {path}
    </svg>
  );
}

const TodayIcon = () =>
  stroke(
    <>
      <path d="M3.5 10.5h17a8.5 8.5 0 0 1-17 0Z" />
      <path d="M9.4 3.4c-.8 1 .8 1.8 0 2.9" />
      <path d="M14.2 3.4c-.8 1 .8 1.8 0 2.9" />
    </>,
  );

const MenuIcon = () =>
  stroke(
    <>
      <rect x="3.5" y="5.5" width="17" height="15" rx="3.5" />
      <path d="M3.5 10h17" />
      <path d="M8.5 3.2v4M15.5 3.2v4" />
    </>,
  );

const OrdersIcon = () =>
  stroke(
    <>
      <rect x="5" y="3" width="14" height="18" rx="2.5" />
      <path d="M8.5 8h7M8.5 12h7M8.5 16h4.5" />
    </>,
  );

const AlertsIcon = () =>
  stroke(
    <>
      <path d="M18 9a6 6 0 1 0-12 0c0 4.6-1.8 6.1-1.8 6.1h15.6S18 13.6 18 9Z" />
      <path d="M10.2 18.4a2.2 2.2 0 0 0 3.6 0" />
    </>,
  );

const TABS = [
  { to: '/', Icon: TodayIcon, label: 'Home' },
  { to: '/menu', Icon: MenuIcon, label: 'Menu' },
  { to: '/history', Icon: OrdersIcon, label: 'Orders' },
  { to: '/alerts', Icon: AlertsIcon, label: 'Alerts' },
];

/**
 * The frame, and nothing else.
 *
 * Each screen draws its own head — the home screen opens on a dark panel, the
 * weekly menu on a plain heading — so the shell deliberately has none of its
 * own. One chrome bar across all of them would flatten exactly the difference
 * the design is making.
 *
 * Height is 100dvh, not 100vh: mobile browsers shrink the visual viewport when
 * the address bar collapses, and 100vh would push the tab bar under it.
 */
export default function AppShell({ children }: { children: ReactNode }) {
  const { t } = useT();
  const { unread } = useNotifications();
  const user = useAuthStore((s) => s.user);
  const profile = useProfile();
  const where = [profile?.siteName, profile?.pointName].filter(Boolean).join(' · ');

  const topLink = ({ isActive }: NavLinkRenderProps) =>
    `min-h-11 flex items-center text-[16.5px] font-bold ${
      isActive ? 'text-brand' : 'text-muted hover:text-ink'
    }`;

  return (
    <div className="flex h-[100dvh] flex-col bg-canvas">
      {/* Wide layout keeps a top bar; the phone gets the tab bar below. */}
      <header className="hidden shrink-0 flex-wrap items-center gap-3.5 border-b border-line bg-white px-7 py-[18px] md:flex">
        <NavLink to="/" end className="flex items-center gap-2.5">
          <Logo />
          <b className="text-[17px] tracking-[-0.03em] md:text-[18.5px]">MealDesk</b>
        </NavLink>
        <nav className="ml-5 flex flex-wrap gap-6">
          {TABS.map((tab) => (
            <NavLink key={tab.to} to={tab.to} end={tab.to === '/'} className={topLink}>
              {t(tab.label)}
            </NavLink>
          ))}
        </nav>
        <NavLink to="/profile" className="ml-auto text-[14px] md:text-[15px] text-muted hover:text-ink">
          {[user?.name, where].filter(Boolean).join(' · ')}
        </NavLink>
      </header>

      <main className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        <div className="mx-auto w-full max-w-[430px] pb-6 md:max-w-[880px] md:px-2 md:py-5 md:pb-12">
          {children}
        </div>
      </main>

      {/*
        Anchored to the edge, the way a native tab bar is, rather than floating
        over the content. In the flex column rather than fixed: a fixed bar has
        to be paid for with bottom padding on every screen, and that padding is
        a number somebody has to remember to change when the bar does.

        The safe-area inset is padding inside the bar, so on a phone with a home
        indicator the white runs to the bottom of the glass and the labels sit
        above it.
      */}
      <nav className="shrink-0 border-t border-line bg-white pb-[env(safe-area-inset-bottom)] md:hidden">
        <div className="mx-auto grid max-w-[430px] grid-cols-4">
          {TABS.map((tab) => (
            <NavLink
              key={tab.to}
              to={tab.to}
              end={tab.to === '/'}
              className="flex min-h-[62px] flex-col items-center justify-center gap-[5px] pt-2 pb-1.5"
            >
              {({ isActive }: NavLinkRenderProps) => (
                <>
                  {/* Three cues, not one. The green and the grey measure 0.14
                      and 0.15 in luminance — near identical, so to a red-green
                      eye the colour says nothing. The capsule and the weight
                      are what actually carry the state. */}
                  <span
                    className={`relative flex h-[30px] w-[46px] items-center justify-center rounded-[11px] ${
                      isActive ? 'bg-brand-soft text-brand-ink' : 'text-subtle'
                    }`}
                  >
                    <tab.Icon />
                    {tab.to === '/alerts' && unread > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 size-2.5 rounded-full bg-[#f05252] ring-2 ring-white" />
                    )}
                  </span>
                  <b
                    className={`text-[12.5px] leading-none ${
                      isActive ? 'font-extrabold text-brand-ink' : 'font-medium text-subtle'
                    }`}
                  >
                    {t(tab.label)}
                  </b>
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}

function Logo() {
  return (
    <span
      aria-hidden
      className="flex size-[38px] shrink-0 items-center justify-center rounded-[13px] bg-brand-bright"
    >
      <svg width="21" height="21" viewBox="0 0 24 24" fill="none">
        <path d="M20 4c0 9-5.5 13-10.5 13S4 15 4 15s2-9 9.5-10C17 4.6 20 4 20 4Z" fill="#fff" />
        <path d="M12.5 8.5 6 20" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    </span>
  );
}
